import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Card,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import ViolationProfileCard from "./ViolationProfileCard";
import ViolationDetailsTab from "./ViolationDetailsTab";
import ViolationNoticeTab from "./ViolationNoticeTab";
import ViolationCalendarTab from "./ViolationCalendarTab";
import ViolationCommentsTab from "./ViolationCommentsTab";
import ViolationVisitsTab from "./ViolationVisitsTab";
import ViolationPrintContent from "./ViolationPrintContent";
import ViolationFinesTab from "./ViolationFines";
import ViolationAppealsTab from "./ViolationAppealsTab";
import ViolationLinksTab from "./ViolationLinksTab";
import ViolationQRSurveyTab from "./ViolationQRSurveyTab";
import ViolationStatusHistoryTab from "./ViolationStatusHistoryTab";
import ViolationCureDaysPanel from "./ViolationCureDaysPanel";

import api, { API_BASE_URL } from "../../../src/utils/api";

const ViolationsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();

  const [violationDetails, setViolationDetails] = useState(null);
  const [member, setMember] = useState(null);
  const [violationType, setViolationType] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [imageURL, setImageURL] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [printData, setPrintData] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const storedUser = sessionStorage.getItem("user");
  let currentUserId = null;

  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      currentUserId = parsedUser?.userId ?? null;
    } catch (e) {
      console.error("Failed to parse user from sessionStorage", e);
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const [violationResp, usersResp] = await Promise.all([
          api.get(`/getViolation/${id}`),
          api.get("/users-by-role"),
        ]);

        const violationData = violationResp.data;

        let memberData = null;
        if (violationData.property_id) {
          const memberResp = await api.get(`/members/${violationData.property_id}`);
          memberData = memberResp.data;
        }

        setViolationDetails(violationData);
        setMember(memberData);
        setUsers(usersResp.data || []);

        const filesResp = await api.get(`/violation-files/${id}`);
        const files = filesResp.data || [];

        const imageFile = files.find((f) => f.file_type?.startsWith("image/"));
        if (imageFile) {
          const fileNameOnly = imageFile.file_name;
          setImageURL(
            `${API_BASE_URL}/violationsimages/${id}/${encodeURIComponent(
              fileNameOnly
            )}`
          );
        }

        if (violationData.ccr_violation_type_id) {
          const types = await api.get(`/getCcrViolationTypes`);
          const match = types.data.find(
            (t) => t.id === violationData.ccr_violation_type_id
          );
          setViolationType(match?.title || "Type Not Found");
        }
      } catch (err) {
        console.error("Error loading violation details:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleTabChange = (_, newVal) => setActiveTab(newVal);

  useEffect(() => {
    if (!isPrinting || !printData) return;
    const timer = setTimeout(() => {
      const printContents = printRef.current.innerHTML;
      const printWindow = window.open("", "_blank", "width=960,height=900");
      if (!printWindow) { setIsPrinting(false); return; }
      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Violation Report #${violationDetails?.id || ""}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { background: #fff; font-family: 'Segoe UI', Arial, sans-serif; color: #1a202c; }
    body { padding: 0; }
    img { max-width: 100%; display: block; }
    table { border-collapse: collapse; }
    @media print {
      html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      .no-print { display: none !important; }
    }
    .print-btn-bar { position: fixed; top: 0; right: 0; z-index: 9999; padding: 10px 16px; background: rgba(255,255,255,0.95); border-bottom-left-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); display: flex; gap: 8px; }
    .print-btn { background: #2f63ad; color: #fff; border: none; padding: 8px 20px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; }
    .print-btn:hover { background: #1e3a5f; }
    .close-btn { background: #6b7280; color: #fff; border: none; padding: 8px 16px; border-radius: 6px; font-size: 14px; cursor: pointer; font-family: inherit; }
  </style>
</head>
<body>
  <div class="print-btn-bar no-print">
    <button class="print-btn" onclick="window.print()">🖨 Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">Close</button>
  </div>
  <div style="padding-top: 56px;">
    ${printContents}
  </div>
  <script>
    // wait for images then auto-open print dialog
    var imgs = document.images;
    var loaded = 0;
    var total = imgs.length;
    function tryPrint() { if (++loaded >= total) { setTimeout(function(){ window.print(); }, 400); } }
    if (total === 0) { setTimeout(function(){ window.print(); }, 400); }
    else { for (var i = 0; i < total; i++) { if (imgs[i].complete) { tryPrint(); } else { imgs[i].onload = tryPrint; imgs[i].onerror = tryPrint; } } }
  </script>
</body>
</html>`);
      printWindow.document.close();
      printWindow.focus();
      setIsPrinting(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [isPrinting, printData]);

  const handlePrint = async () => {
    try {
      const [filesResp, finesResp, letterResp, statusResp] = await Promise.all([
        api.get(`/violation-files/${id}`).catch(() => ({ data: [] })),
        api.get(`/violation-fines/violation/${id}`).catch(() => ({ data: [] })),
        api.get(`/violations/${id}/letter-logs`).catch(() => ({ data: [] })),
        api.get(`/violations/${id}/status-history`).catch(() => ({ data: [] })),
      ]);
      setPrintData({
        allFiles: filesResp.data || [],
        fines: finesResp.data || [],
        letterLogs: letterResp.data || [],
        statusHistory: statusResp.data || [],
      });
      setIsPrinting(true);
    } catch (err) {
      console.error("Error fetching print data:", err);
    }
  };

  const handleSendEmail = async () => {
    setIsSending(true);
    try {
      await api.post("/send-violation-email", {
        violationId: id,
        recipientEmail,
        ccEmail: ccEmail || null,
        recipientAddress: member?.FullAddress || "",
        ownerName: `${member?.FirstName || ""} ${member?.LastName || ""}`.trim(),
      });

      setSnackbar({
        open: true,
        message: "✅ Email sent successfully.",
        severity: "success",
      });
      setEmailDialogOpen(false);
    } catch (err) {
      console.error("Send email error:", err);
      setSnackbar({
        open: true,
        message: "❌ Failed to send email.",
        severity: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const textFieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!violationDetails) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        Failed to load data.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 2.5 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={secondaryButtonSx}
        >
          Back
        </Button>
      </Box>

      <Box sx={{ mb: 3.5 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Violation Details
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Review violation details, notice, calendar activity, comments, visits,
          and fines.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...pageCardSx, p: 2 }}>
            <ViolationProfileCard member={member} violation_id={id} />
          </Card>
          {violationDetails && (
            <Box sx={{ mt:2 }}>
              <ViolationCureDaysPanel violationId={id} violationDetails={violationDetails} />
            </Box>
          )}
        </Grid>

        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              ...pageCardSx,
              overflow: "hidden",
              borderTop: "4px solid #d97706",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                pt: 2.25,
                pb: 1.5,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.02)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <WarningAmberIcon sx={{ color: "#d97706" }} />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#223142" }}
                    >
                      Violation #{violationDetails?.id}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b778c",
                    }}
                  >
                    {violationType || "Violation"}{" "}
                    {violationDetails?.status
                      ? `• ${violationDetails.status}`
                      : ""}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    flexWrap: "wrap",
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<EmailIcon />}
                    onClick={() => setEmailDialogOpen(true)}
                    sx={primaryButtonSx}
                  >
                    Send via Email
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={secondaryButtonSx}
                  >
                    Print
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 3,
                  minHeight: "auto",
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    minHeight: 40,
                    color: "#5f6b7a",
                    mr: 1,
                    px: { xs: 1.5, md: 2 },
                    py: 1,
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  },
                  "& .Mui-selected": {
                    color: "#2f63ad !important",
                    backgroundColor: "rgba(47,99,173,0.08)",
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                <Tab label="Details" />
                <Tab label="Notice" />
                <Tab label="Calendar" />
                <Tab label="Comments" />
                <Tab label="Visits" />
                <Tab label="Fines" />
                <Tab label="Appeals" />
                <Tab label="History" />
                <Tab label="Links" />
                <Tab label="QR & Survey" />
              </Tabs>

              <Divider sx={{ mb: 3, borderColor: "rgba(47,99,173,0.08)" }} />

              {activeTab === 0 && (
                <ViolationDetailsTab
                  violationDetails={violationDetails}
                  imageURL={imageURL}
                  violationType={violationType}
                  users={users}
                  onAssign={(newAssigneeId) => {
                    setViolationDetails((prev) => ({
                      ...prev,
                      resolved_by: newAssigneeId,
                    }));
                  }}
                />
              )}

              {activeTab === 1 && (
                <ViolationNoticeTab
                  member={member}
                  violationDetails={violationDetails}
                  violationType={violationType}
                  imageURL={imageURL}
                  currentUserId={currentUserId}
                />
              )}

              {activeTab === 2 && (
                <ViolationCalendarTab
                  violation_id={id}
                  currentUserId={currentUserId}
                />
              )}

              {activeTab === 3 && (
                <ViolationCommentsTab
                  violation_id={id}
                  currentUserId={currentUserId}
                />
              )}

              {activeTab === 4 && (
                <ViolationVisitsTab
                  violation_id={id}
                  currentUserId={currentUserId}
                />
              )}

              {activeTab === 5 && (
                <ViolationFinesTab
                  violation_id={id}
                  currentUserId={currentUserId}
                  member={member}
                  users={users}
                  violationCreatedAt={
                    violationDetails?.created_at || violationDetails?.requested_at
                  }
                />
              )}
              {activeTab === 6 && <ViolationAppealsTab violationId={id} />}
              {activeTab === 7 && <ViolationStatusHistoryTab violationId={id} />}
              {activeTab === 8 && <ViolationLinksTab violationId={id} />}
              {activeTab === 9 && <ViolationQRSurveyTab violationId={id} />}
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Send Violation via Email
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Recipient Email"
            fullWidth
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={textFieldSx}
          />

          <TextField
            margin="dense"
            label="CC Email (optional)"
            fullWidth
            type="email"
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
            sx={textFieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "flex-end",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setEmailDialogOpen(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSendEmail}
            disabled={isSending || !recipientEmail}
            variant="contained"
            sx={primaryButtonSx}
          >
            {isSending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div style={{ display: "none" }}>
        <div ref={printRef}>
          <ViolationPrintContent
            violationDetails={violationDetails}
            member={member}
            violationType={violationType}
            imageURL={imageURL}
            allFiles={printData?.allFiles || []}
            fines={printData?.fines || []}
            letterLogs={printData?.letterLogs || []}
            statusHistory={printData?.statusHistory || []}
          />
        </div>
      </div>
    </Box>
  );
};

export default ViolationsDetails;